import {useCallback, useMemo, useRef, useState} from "react";
import {Button, Checkbox, InputGroup, Popover} from "@blueprintjs/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import styled from "@emotion/styled";

export type ChoosableColumn = {
  colId: string;
  headerName: string;
  pinned: boolean;
};

type StopColumnChooserProps = {
  columns: ChoosableColumn[];
  hiddenColumnIds: Set<string>;
  onToggle: (colId: string) => void;
  onReorder: (columns: ChoosableColumn[]) => void;
};

const StopColumnChooser = ({columns, hiddenColumnIds, onToggle, onReorder}: StopColumnChooserProps) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  const filteredColumns = useMemo(() => {
    if (!searchText.trim()) return columns;
    const query = searchText.toLowerCase();
    return columns.filter((col) => col.headerName.toLowerCase().includes(query));
  }, [columns, searchText]);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    dragNodeRef.current = e.currentTarget as HTMLDivElement;
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (dragIndex === null || columns[index].pinned) return;
      setDragOverIndex(index);
    },
    [dragIndex, columns]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === dropIndex || columns[dropIndex].pinned) return;

      const reordered = [...columns];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(dropIndex, 0, moved);
      onReorder(reordered);
      setDragIndex(null);
      setDragOverIndex(null);
    },
    [dragIndex, columns, onReorder]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  return (
    <Popover
      content={
        <PopoverContainer data-testid="stop-column-chooser-list">
          <ListHeader>
            <ListTitle>Show/Hide Stop Columns</ListTitle>
            <SearchInput
              placeholder="Search columns..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              leftIcon="search"
              rightElement={
                searchText ? (
                  <Button variant="minimal" icon="small-cross" size="small" onClick={() => setSearchText("")} />
                ) : undefined
              }
              small
            />
          </ListHeader>
          <ColumnList>
            {filteredColumns.map((col) => {
              const originalIndex = columns.indexOf(col);
              return (
                <ColumnRow
                  key={col.colId}
                  draggable={!col.pinned}
                  onDragStart={(e) => handleDragStart(e, originalIndex)}
                  onDragOver={(e) => handleDragOver(e, originalIndex)}
                  onDrop={(e) => handleDrop(e, originalIndex)}
                  onDragEnd={handleDragEnd}
                  isDragOver={dragOverIndex === originalIndex}
                  isDragging={dragIndex === originalIndex}
                  isPinned={col.pinned}
                >
                  <StyledCheckbox
                    checked={col.pinned || !hiddenColumnIds.has(col.colId)}
                    onChange={() => onToggle(col.colId)}
                    disabled={col.pinned}
                  />
                  <DragHandle isPinned={col.pinned}>
                    <FontAwesomeIcon
                      icon={solid("grip-dots-vertical")}
                      size="xs"
                      color={col.pinned ? "#D1D5DB" : "#9CA3AF"}
                    />
                  </DragHandle>
                  <ColumnName onClick={() => !col.pinned && onToggle(col.colId)} isPinned={col.pinned}>
                    {col.headerName}
                  </ColumnName>
                </ColumnRow>
              );
            })}
          </ColumnList>
        </PopoverContainer>
      }
      position="bottom-right"
      minimal
    >
      <ChooserButton
        data-testid="stop-column-chooser-btn"
        variant="minimal"
        title="Columns"
        icon={<FontAwesomeIcon icon={solid("line-columns")} style={{width: "16px", height: "14px"}} />}
      />
    </Popover>
  );
};

export default StopColumnChooser;

const PopoverContainer = styled.div`
  min-width: 240px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
`;

const ListHeader = styled.div`
  padding: 12px 16px 8px;
  flex-shrink: 0;
`;

const ListTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 8px;
  color: #1c2127;
`;

const SearchInput = styled(InputGroup)`
  .bp5-input {
    border: 1px solid #d1d5db;
    border-radius: 6px;
    box-shadow: none;
    font-size: 12px;

    &:focus {
      border-color: #2196f3;
      box-shadow: 0 0 0 1px #2196f3;
    }
  }

  .bp5-input-left-container .bp5-icon {
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const ColumnList = styled.div`
  padding: 0 16px 12px;
  overflow-y: auto;
  flex: 1;
`;

const ColumnRow = styled.div<{isDragOver: boolean; isDragging: boolean; isPinned: boolean}>`
  display: flex;
  align-items: center;
  padding: 4px 0;
  cursor: default;
  opacity: ${({isDragging}) => (isDragging ? 0.4 : 1)};
  border-top: ${({isDragOver}) => (isDragOver ? "2px solid #2b95d6" : "2px solid transparent")};
`;

const DragHandle = styled.span<{isPinned: boolean}>`
  display: flex;
  align-items: center;
  margin: 0 12px 0 4px;
  cursor: ${({isPinned}) => (isPinned ? "default" : "grab")};
`;

const StyledCheckbox = styled(Checkbox)`
  margin: 0 !important;

  .bp5-control-indicator {
    background: #e0e0e0 !important;
    box-shadow: none !important;
  }

  &.bp5-control input:checked ~ .bp5-control-indicator {
    background: #2196f3 !important;
  }

  &.bp5-disabled .bp5-control-indicator {
    opacity: 0.5;
  }
`;

const ColumnName = styled.span<{isPinned: boolean}>`
  font-size: 13px;
  user-select: none;
  cursor: ${({isPinned}) => (isPinned ? "default" : "pointer")};
`;

const ChooserButton = styled(Button)`
  &.bp5-button {
    box-shadow: none !important;
  }
`;
